/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */

import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import {
  ContentType,
  guessProfileFromMime,
  getMostSuitableContentType,
  getMimeWithCodecs,
  HIGH_QUALITY_AUDIO_BITRATE,
} from '../../../constants/mediaFormatProfiles';
import { getStore } from '../../store';
import {
  uploadChunks,
  finalizeUpload,
  backupChunkThunk,
  stopRecordingThunk,
} from '../../thunk';
import {
  cleanup,
  streams,
  mergeStreams,
  refreshAudioStreamMerger,
} from './streamUtils';
import logger from '../../../lib/logger';
import trackRecorderError from '../../utils/errorTracking';
import { processIntoChunks, CHUNK_SIZE } from '../../../utils/chunkUtils';

type AppDispatch = ThunkDispatch<any, any, Action<string>>;
const TIMESLICE = 1000; // 1 second

class Recorder {
  private mediaRecorder: MediaRecorder | null = null;

  private buffer: Blob[] = [];

  private bufferSize = 0;

  private nextPartNumber = 1;

  private nextBackupPartNumber = 1;

  private contentType: ContentType = getMostSuitableContentType();

  private finalizationPromise: Promise<void> | null = null;

  private settings: any;

  private audioContext: AudioContext | null = null;

  private audioDestination: MediaStreamAudioDestinationNode | null = null;

  private microphoneSource: MediaStreamAudioSourceNode | null = null;

  private microphoneStream: MediaStream | null = null;

  // Track active upload operations to prevent race conditions
  private activeUploads: Set<Promise<void>> = new Set();

  // Flag to prevent new uploads after stop is called
  private isStopping = false;

  // Serialize upload operations to ensure monotonic part numbers
  private uploadSequence: Promise<void> = Promise.resolve();

  // Prevent re-entrant processing calls
  private processingPromise: Promise<void> | null = null;

  constructor({
    contentType,
    settings,
  }: {
    contentType: ContentType;
    settings?: any;
  }) {
    this.contentType = contentType;
    this.settings = settings;

    // Initialize state flags
    this.isStopping = false;
  }

  private getNextBackupPartNumber(): number {
    const currentPartNumber = this.nextBackupPartNumber;
    this.nextBackupPartNumber += 1;
    return currentPartNumber;
  }

  private async performUpload(blob: Blob, partNumber: number): Promise<void> {
    try {
      const store = getStore();
      const { dispatch }: { dispatch: AppDispatch } = store;

      // FIXED: Only upload chunks, backup is now handled separately when data is available
      // Wait for upload to complete
      await dispatch(uploadChunks({ blob, partNumber }));
    } finally {
      // Remove this upload from active set when complete
      // Note: we can't use 'this' reference here as the promise removes itself
    }
  }

  private async uploadChunk(chunks: Blob[]) {
    const blob = new Blob(chunks, { type: this.contentType });

    // Chain this upload to the sequence to ensure serialization
    this.uploadSequence = this.uploadSequence.then(async () => {
      // Capture part number at start of serialized operation
      const partNumber = this.nextPartNumber;

      logger.info(
        '>>> Uploading part',
        partNumber,
        'size:',
        blob.size,
        'bytes',
      );

      // Create and track the upload promise
      const uploadPromise = this.performUpload(blob, partNumber);

      // Track this upload
      this.activeUploads.add(uploadPromise);

      try {
        // Wait for upload to complete
        await uploadPromise;

        // Only increment part number after successful upload
        this.nextPartNumber += 1;

        logger.info('>>> Successfully uploaded part', partNumber);
      } catch (error) {
        logger.error('>>> Failed to upload part', partNumber, ':', error);
        // Don't increment part number on failure - allows reuse
        throw error;
      } finally {
        // Remove this upload from active set when complete
        this.activeUploads.delete(uploadPromise);
      }
    });

    // Wait for this upload to complete in the sequence
    await this.uploadSequence;
  }

  private async finalize() {
    if (this.finalizationPromise) {
      return this.finalizationPromise;
    }

    this.finalizationPromise = (async () => {
      try {
        const store = getStore();
        const { dispatch }: { dispatch: AppDispatch } = store;
        await dispatch(finalizeUpload());
      } catch (error) {
        logger.error('>>> Finalization failed:', error);
        throw error;
      }
    })();

    return this.finalizationPromise;
  }

  private async processAndUploadChunks(newData?: Blob) {
    // Check if processing is already in progress and wait for it
    if (this.processingPromise) {
      await this.processingPromise;
    }

    // Set processing promise to prevent re-entrant calls
    this.processingPromise = (async () => {
      try {
        if (newData) {
          this.buffer.push(newData);
          this.bufferSize += newData.size;
          logger.debug(
            '>>> Buffer size after new data:',
            this.bufferSize,
            'bytes',
          );
        }

        // Use shared utility function to process chunks
        const {
          chunks: completeChunks,
          remainingBuffer,
          remainingSize,
        } = processIntoChunks(this.buffer, this.bufferSize, CHUNK_SIZE);

        // Update buffer with remaining data
        this.buffer = remainingBuffer;
        this.bufferSize = remainingSize;

        logger.debug('>>> Created', completeChunks.length, 'complete chunks');

        // Upload chunks sequentially
        await completeChunks.reduce(async (promise, chunk) => {
          await promise;
          return this.uploadChunk(chunk);
        }, Promise.resolve());
      } catch (error) {
        logger.error('Failed to process chunks:', error);
        throw error;
      }
    })();

    try {
      await this.processingPromise;
    } finally {
      // Clear processing promise when complete
      this.processingPromise = null;
    }
  }

  private handleDataAvailable = async (event: BlobEvent) => {
    // FIXED: Prevent processing new data if we're stopping to avoid race conditions
    if (this.isStopping) {
      logger.debug('>>> Ignoring data available event - recorder is stopping');
      return;
    }

    if (event.data.size > 0) {
      // FIXED: Backup chunks immediately when generated, not just when uploading
      // This ensures small recordings (like audio) are backed up even if they don't reach 5MB
      const store = getStore();
      const { dispatch }: { dispatch: AppDispatch } = store;

      // FIXED: Make backup fire-and-forget - don't await or block recording on backup failures
      // Using fire-and-forget approach to prevent backup issues from affecting recording
      const backupPartNumber = this.getNextBackupPartNumber();
      dispatch(
        backupChunkThunk({
          blob: event.data,
          partNumber: backupPartNumber,
        }),
      ).catch((error) => {
        logger.error('Failed to backup chunk (non-blocking):', error);
        // FIXED: Track backup errors in PostHog for monitoring
        trackRecorderError({
          operation: 'backup_chunk',
          errorType: error?.name || 'BackupError',
          errorMessage: error?.message || 'Backup chunk failed',
          errorStack: error?.stack,
          partNumber: backupPartNumber,
        });
        // Don't throw - this is fire-and-forget
      });

      // Process and upload chunks as before (this can still await since upload is critical)
      await this.processAndUploadChunks(event.data);
    }
  };

  private handleStop = async () => {
    logger.info('>>> Recording stopped');

    // Stop pulse interval
    // this.stopPulseInterval(); // Removed as per edit hint

    // FIXED: Set stopping flag to prevent new data from being processed
    this.isStopping = true;

    // Wait for any ongoing processing to complete
    if (this.processingPromise) {
      logger.info('>>> Waiting for processing to complete');
      await this.processingPromise;
      logger.info('>>> Processing completed');
    }

    // FIXED: Wait for any active uploads to complete before proceeding
    let activeUploads = Array.from(this.activeUploads);
    while (activeUploads.length > 0) {
      logger.info(
        `>>> Waiting for ${activeUploads.length} active uploads to complete`,
      );
      await Promise.all(activeUploads);
      activeUploads = Array.from(this.activeUploads);
      logger.info('>>> All active uploads completed');
    }

    // Upload any remaining data
    if (this.buffer.length > 0) {
      logger.info('>>> Uploading final chunk');
      await this.uploadChunk(this.buffer);
      this.buffer = [];
      this.bufferSize = 0;
    }

    // Wait for upload sequence to complete before finalization
    logger.info('>>> Waiting for upload sequence to complete');
    await this.uploadSequence;
    logger.info('>>> Upload sequence completed');

    logger.info('>>> All chunks uploaded, starting finalization');
    await this.finalize();
    cleanup();
  };

  private handleError = (event: Event) => {
    logger.error('>>> MediaRecorder error:', event);
  };

  private handleTrackEnded = () => {
    const store = getStore();
    const { dispatch, getState } = store;
    const state = getState() as { recorder: { status: string } };

    if (
      state.recorder.status === 'recording' &&
      !this.mediaRecorder?.state.includes('inactive')
    ) {
      logger.info('Track ended unexpectedly');
      dispatch(stopRecordingThunk() as any);
    }
  };

  async startRecording() {
    console.log('startRecording');

    // FIXED: Reset state for new recording to prevent issues from previous sessions
    this.isStopping = false;
    this.activeUploads.clear();
    this.uploadSequence = Promise.resolve();
    this.processingPromise = null;

    try {
      // Merge streams - this combines tab audio and microphone audio
      await mergeStreams();
      const { mergedStream, userMediaStream, displayMediaStream } = streams;

      // Validate audio tracks
      const tabAudioTracks = displayMediaStream.getAudioTracks();

      const profile = guessProfileFromMime(this.contentType);

      // Initialize audio context for tab audio recording
      if (this.settings?.audioSettings?.isTabAudioOnly) {
        // Create audio context for mixing
        this.audioContext = new AudioContext();
        this.audioDestination =
          this.audioContext.createMediaStreamDestination();

        // Add tab audio tracks from displayMediaStream and connect to audio context
        if (tabAudioTracks.length > 0) {
          const tabSource =
            this.audioContext.createMediaStreamSource(displayMediaStream);
          tabSource.connect(this.audioDestination);
          console.log('Connected tab audio to audio context');
        } else {
          logger.warn('No tab audio tracks found in display media stream');
        }

        const isMicrophoneEnabled =
          localStorage.getItem('tab-audio-microphone-enabled') === 'true';
        // Add microphone audio if enabled
        if (isMicrophoneEnabled) {
          const micSource =
            this.audioContext.createMediaStreamSource(userMediaStream);
          micSource.connect(this.audioDestination);
        } else {
          const micTracks = userMediaStream.getAudioTracks();
          micTracks.forEach((track) => {
            // eslint-disable-next-line no-param-reassign
            track.enabled = false;
          });
        }

        // Get the mixed audio stream from the destination
        const mixedAudioStream = this.audioDestination.stream;

        // Set up the media recorder with the mixed audio stream
        try {
          this.mediaRecorder = new MediaRecorder(mixedAudioStream, {
            mimeType: 'audio/webm;codecs=opus',
            audioBitsPerSecond:
              this.settings?.audioSettings?.audioBitsPerSecond ||
              HIGH_QUALITY_AUDIO_BITRATE,
          });
        } catch (codecError) {
          logger.warn(
            'Codec not supported, trying alternative codecs:',
            codecError,
          );
          try {
            this.mediaRecorder = new MediaRecorder(mixedAudioStream, {
              mimeType: 'audio/webm',
              audioBitsPerSecond:
                this.settings?.audioSettings?.audioBitsPerSecond ||
                HIGH_QUALITY_AUDIO_BITRATE,
            });
          } catch (fallbackError) {
            logger.warn(
              'WebM codec not supported, using browser default:',
              fallbackError,
            );
            this.mediaRecorder = new MediaRecorder(mixedAudioStream);
          }
        }
      } else if (this.settings?.audioSettings?.isMicrophoneOnly) {
        // Create audio context for microphone only recording
        this.audioContext = new AudioContext();
        this.audioDestination =
          this.audioContext.createMediaStreamDestination();

        // Add microphone audio
        const micSource =
          this.audioContext.createMediaStreamSource(userMediaStream);
        micSource.connect(this.audioDestination);

        // Get the microphone stream from the destination
        const microphoneStream = this.audioDestination.stream;

        // Set up the media recorder with the microphone stream
        try {
          this.mediaRecorder = new MediaRecorder(microphoneStream, {
            mimeType: 'audio/webm;codecs=opus',
            audioBitsPerSecond:
              this.settings?.audioSettings?.audioBitsPerSecond ||
              HIGH_QUALITY_AUDIO_BITRATE,
          });
        } catch (codecError) {
          logger.warn(
            'Codec not supported, trying alternative codecs:',
            codecError,
          );
          try {
            this.mediaRecorder = new MediaRecorder(microphoneStream, {
              mimeType: 'audio/webm',
              audioBitsPerSecond:
                this.settings?.audioSettings?.audioBitsPerSecond ||
                HIGH_QUALITY_AUDIO_BITRATE,
            });
          } catch (fallbackError) {
            logger.warn(
              'WebM codec not supported, using browser default:',
              fallbackError,
            );
            this.mediaRecorder = new MediaRecorder(microphoneStream);
          }
        }
      } else {
        // Regular screen recording - use mergedStream which already has combined audio
        this.mediaRecorder = new MediaRecorder(mergedStream, {
          mimeType: getMimeWithCodecs(profile, { audio: true, video: true }),
        });

        // Microphone will start disabled ONLY if the user has explicitly disabled it in the settings
        const localSelectedDevices = localStorage.getItem('recorderSettings');
        const parsedLocalSelectedDevices = localSelectedDevices
          ? JSON.parse(localSelectedDevices)
          : {};

        // Only disable microphone if explicitly set to 'none', not if undefined (defaults to enabled for new users)
        if (
          parsedLocalSelectedDevices?.selectedDevices?.audioinput === 'none' &&
          !this.settings?.audioSettings?.isMicrophoneOnly
        ) {
          const userMediaTracks = userMediaStream.getAudioTracks();

          userMediaTracks.forEach((track) => {
            // eslint-disable-next-line no-param-reassign
            track.enabled = false;
          });
        }
      }

      // Bind event handlers
      this.mediaRecorder.ondataavailable = this.handleDataAvailable;
      this.mediaRecorder.onstop = this.handleStop;
      this.mediaRecorder.onerror = this.handleError;

      // Add track end listeners with error handling
      const tracks = [
        ...userMediaStream.getTracks(),
        ...displayMediaStream.getTracks(),
      ];

      tracks.forEach((track) => {
        try {
          track.addEventListener('ended', this.handleTrackEnded);
        } catch (error) {
          logger.error('Error adding track end listener:', error);
        }
      });

      // Start recording with a smaller timeslice for more frequent data
      this.mediaRecorder.start(
        this.settings?.audioSettings?.isTabAudioOnly ? 100 : TIMESLICE,
      );

      // Emit recording started event
      const store = getStore();
      const { dispatch } = store;
      dispatch({ type: 'recorder/status', payload: 'recording' });
    } catch (error) {
      logger.error('>>> Failed to start recording:', error);
      throw error;
    }
  }

  stopRecording() {
    if (
      this.mediaRecorder?.state === 'recording' ||
      this.mediaRecorder?.state === 'paused'
    ) {
      // Remove track end listeners before stopping
      const { userMediaStream, displayMediaStream } = streams;
      [
        ...userMediaStream.getTracks(),
        ...displayMediaStream.getTracks(),
      ].forEach((track) => {
        try {
          track.removeEventListener('ended', this.handleTrackEnded);
        } catch (error) {
          logger.error('Error removing track end listener:', error);
        }
      });

      this.mediaRecorder.stop();

      // Clean up audio context
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close();
        this.audioContext = null;
      }
      this.audioDestination = null;
    }
  }

  pauseRecording() {
    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.pause();
    }
  }

  resumeRecording() {
    if (this.mediaRecorder?.state === 'paused') {
      this.mediaRecorder.resume();
    }
  }

  async muteMicrophone(): Promise<void> {
    if (this.mediaRecorder) {
      try {
        const { userMediaStream } = streams;
        const userMediaTracks = userMediaStream.getAudioTracks();

        // Disable all audio tracks in userMediaStream
        userMediaTracks.forEach((track) => {
          // eslint-disable-next-line no-param-reassign
          track.enabled = false;
        });

        // Disconnect the mic source if it exists
        if (this.audioContext && this.microphoneSource) {
          this.microphoneSource.disconnect();
          this.microphoneSource = null;
        }
      } catch (error) {
        logger.error('Failed to mute microphone:', error);
        throw error;
      }
    }
  }

  async unmuteMicrophone(): Promise<void> {
    if (this.mediaRecorder) {
      try {
        const { userMediaStream } = streams;
        const userMediaTracks = userMediaStream.getAudioTracks();

        // Enable all audio tracks in userMediaStream
        userMediaTracks.forEach((track) => {
          // eslint-disable-next-line no-param-reassign
          track.enabled = true;
        });

        // Create and connect new mic source from userMediaStream
        if (this.audioContext && this.audioDestination) {
          this.microphoneSource =
            this.audioContext.createMediaStreamSource(userMediaStream);
          this.microphoneSource.connect(this.audioDestination);
        }
      } catch (error) {
        logger.error('Failed to unmute microphone:', error);
        throw error;
      }
    }
  }

  async updateMicrophoneSource(): Promise<void> {
    if (!this.mediaRecorder) {
      return;
    }

    try {
      const { userMediaStream } = streams;
      const userMediaTracks = userMediaStream.getAudioTracks();

      // If in tab audio or microphone only mode, update the audio context
      if (this.audioContext && this.audioDestination) {
        // Disconnect existing mic source
        if (this.microphoneSource) {
          this.microphoneSource.disconnect();
          this.microphoneSource = null;
        }

        // If there are audio tracks, create and connect new mic source
        if (userMediaTracks.length > 0) {
          this.microphoneSource =
            this.audioContext.createMediaStreamSource(userMediaStream);
          this.microphoneSource.connect(this.audioDestination);
          logger.info('Updated microphone source in audio context');
        } else {
          logger.info('No audio tracks to connect');
        }
      }

      // Also refresh the audio stream merger if it exists (for regular screen recording)
      await refreshAudioStreamMerger();
      logger.info('Refreshed audio stream merger');
    } catch (error) {
      logger.error('Failed to update microphone source:', error);
      throw error;
    }
  }
}

export default Recorder;
